import axios from 'axios';
import { DEFAULT_ROOMS, PUBLIC_ROOMS } from '../mock';

/**
 * Msg #984 — Extracted from ChatPage.jsx (was ~675 lines inside a mega-file).
 *
 * `runChatCommand(raw, deps)` parses a slash / colon-prefixed user input and
 * dispatches to the matching side-effect (HTTP POST to backend, local system
 * message, mutating state via setters, etc.). Returns `true` if the input
 * was handled as a command (and the caller should NOT also send it as chat),
 * `false` if it didn't match any command pattern.
 *
 * All external state / helpers are threaded through the `deps` object so
 * this function can live outside of React and be unit-tested in isolation.
 * The parent captures a fresh deps snapshot on every render — that's cheap
 * because deps is a plain object and only read once at command dispatch time.
 */
export const runChatCommand = (raw, deps) => {
  const {
    // constants
    API,
    // state getters
    username, userId, room, isAdmin, cloaked,
    liveNpcs, ignoredUsers, showTimestamps, lastTyped,
    knownPrivateRooms,
    ALL_NPC_DIRECTORY,
    isMod = false,
    // state setters
    setInput, setMessages, setRoom, setViewChar, setIgnoredUsers,
    setNotifyJoinLeave, setShowTutorial, setShowTimestamps,
    setAlias, setKickModal, setKnownPrivateRooms, setMyColor,
    setPollDelay, setRefreshTick, setRoomDescription, setShowEdit,
    // Msg #987 — Available / Away / RP toggles from slash commands.
    setAvailable, setRpAvailable,
    // helpers defined in ChatPage
    pushLocalSystem, pickResponder, sendToServer, triggerNpc, logout,
    pickColorForUser, sendWhisper, displayName,
  } = deps || {};

  const text = raw.trim();
  const lower = text.toLowerCase();

  // ── /? — open tutorial / help popup ──
  if (text === '/?' || lower === '/help') {
    setShowTutorial(true);
    return true;
  }
  if (text === '/!') { if (lastTyped) setInput(lastTyped); return true; }
  if (/^\/(quit|exit|bye)(\s|$)/i.test(text)) { logout(); return true; }
  if (lower === '/profile') { setShowEdit(true); return true; }
  // Msg #987 — Available / Away / RP status slash commands.
  if (lower === '/available' || lower === '/back') {
    if (setAvailable) setAvailable(true);
    if (setRpAvailable) setRpAvailable(false);
    pushLocalSystem('You are now marked as Available.');
    return true;
  }
  if (lower === '/away' || lower === '/afk') {
    if (setAvailable) setAvailable(false);
    if (setRpAvailable) setRpAvailable(false);
    pushLocalSystem('You are now marked as Away.');
    return true;
  }
  if (lower === '/available_rp' || lower === '/rp' || lower === '/rpready') {
    if (setAvailable) setAvailable(true);
    if (setRpAvailable) setRpAvailable(true);
    pushLocalSystem('You are now advertising as Available for RP (blue orb).');
    return true;
  }
  if (lower === '/available_rp_off' || lower === '/rpoff') {
    if (setRpAvailable) setRpAvailable(false);
    pushLocalSystem('RP-available flag cleared.');
    return true;
  }
  if (lower === '/clear') { setMessages((m) => m.slice(-5)); return true; }
  if (lower === '/timestamp') {
    setShowTimestamps((v) => !v);
    pushLocalSystem(`Timestamps ${showTimestamps ? 'hidden' : 'shown'}.`);
    return true;
  }
  if (lower === '/notify') {
    setNotifyJoinLeave((v) => {
      pushLocalSystem(`Enter/exit notifications ${v ? 'hidden' : 'shown'} for ${room}.`);
      return !v;
    });
    return true;
  }
  let m = text.match(/^\/refresh(?:\s+(\d+))?$/i);
  if (m) {
    const n = m[1] ? parseInt(m[1], 10) : null;
    if (n === null) {
      setPollDelay((d) => {
        const next = d > 0 ? 0 : 2000;
        pushLocalSystem(next === 0 ? 'Refresh paused.' : 'Refresh resumed (2s).');
        return next;
      });
    } else {
      const ms = Math.max(1, n) * 1000;
      setPollDelay(ms);
      pushLocalSystem(`Refresh delay set to ${n}s.`);
    }
    setRefreshTick((t) => t + 1);
    return true;
  }
  m = text.match(/^\/(show|last)(?:\s+(\d+))?$/i);
  if (m) {
    const n = Math.max(5, parseInt(m[2] || '5', 10));
    setMessages((mm) => mm.slice(-n));
    pushLocalSystem(`Showing last ${n} messages.`);
    return true;
  }
  m = text.match(/^\/(color|co)\s+#?([0-9a-fA-F]{3,8})$/);
  if (m) { const hex = (m[2].length === 3 || m[2].length === 6) ? `#${m[2]}` : `#${m[2]}`; setMyColor(hex); pushLocalSystem(`Your text color is now ${hex}.`); return true; }
  m = text.match(/^\/(alias|nick)\s+(.+)$/);
  if (m) { const v = m[2].trim(); if (v.toLowerCase() === 'off') { setAlias(''); pushLocalSystem('Alias cleared.'); } else { setAlias(v); pushLocalSystem(`Alias set to "${v}".`); } return true; }

  // /whois user — looks up NPC, registered profile (logged-on OR off), online presence
  m = text.match(/^\/whois\s+(.+)$/i);
  if (m) {
    const u = m[1].trim();
    axios.get(`${API}/whois`, { params: { username: u } })
      .then((res) => {
        const d = res.data || {};
        if (!d.found) { pushLocalSystem(`No information found for "${u}".`); return; }
        if (d.kind === 'npc') {
          setViewChar({ name: d.username, color: d.color, status: 'NPC',
            description: d.description, picture: d.picture, isNPC: true });
        } else {
          setViewChar({
            name: d.username,
            color: d.color || pickColorForUser(d.username),
            status: d.online ? `Online in ${d.room || '?'}` : 'Offline',
            firstname: d.firstname, lastname: d.lastname, gender: d.gender,
            description: d.description, picture: d.picture, website: d.website,
          });
        }
      })
      .catch(() => pushLocalSystem(`No information found for "${u}".`));
    return true;
  }
  m = text.match(/^\/ignore(?:\s+(-?\s*\S+))?$/i);
  if (m) {
    if (!m[1]) { const arr = Array.from(ignoredUsers); pushLocalSystem(arr.length ? `Ignoring: ${arr.join(', ')}` : 'Your ignore list is empty.'); return true; }
    const arg = m[1].trim(); const remove = arg.startsWith('-'); const u = arg.replace(/^-\s*/, '');
    setIgnoredUsers((s) => { const n = new Set(s); if (remove) n.delete(u); else n.add(u); return n; });
    pushLocalSystem(remove ? `No longer ignoring ${u}.` : `Now ignoring ${u}.`);
    return true;
  }
  m = text.match(/^\/announce\s+(.+)$/i);
  if (m) {
    if (isAdmin) {
      axios.post(`${API}/admin/announce`, { requester: username, message: m[1] })
        .catch(() => pushLocalSystem('Announce failed.'));
    } else {
      pushLocalSystem('Admin only.');
    }
    return true;
  }
  m = text.match(/^\/(msg|to|whisper|wh)\s+(\S+)\s+([\s\S]+)$/i);
  if (m) {
    sendWhisper(m[2], m[3]);
    return true;
  }

  // ── /invite user[,user...] — whisper them an invite to current room ──
  //    Msg #778 — If the target is an NPC/Monster, use silent_join instead
  //    so the NPC simply walks into the caller's room (no whisper counter-
  //    invite, no [[PRIVATE_INVITE]] roundtrip).
  m = text.match(/^\/invite\s+(.+)$/i);
  if (m) {
    const targets = m[1].split(',').map((s) => s.trim()).filter(Boolean);
    if (!targets.length) { pushLocalSystem('Usage: /invite user[,user…]'); return true; }
    targets.forEach((t) => {
      const npcMatch = ALL_NPC_DIRECTORY.find((n) => String(n.name).toLowerCase() === t.toLowerCase());
      if (npcMatch) {
        axios.post(`${API}/npc/silent_join`, {
          requester_user_id: userId,
          requester_name: displayName(),
          npc: npcMatch.name,
          room,
        })
          .then(() => pushLocalSystem(`${npcMatch.name.replace('_',' ')} has joined ${room}.`))
          .catch((err) => {
            const d = err && err.response && err.response.data && err.response.data.detail;
            pushLocalSystem(d || `${npcMatch.name.replace('_',' ')} could not join.`);
          });
      } else {
        sendWhisper(t, `[Invite] Join me in room "${room}". Use /join ${room}.`);
      }
    });
    return true;
  }

  // ── Msg #778 — /askprivate {NPC}   NPC creates a private room and invites you ──
  m = text.match(/^\/askprivate\s+(\S+)/i);
  if (m) {
    const npcName = m[1];
    axios.post(`${API}/npc/ask_private`, {
      requester_user_id: userId,
      requester_name: displayName(),
      npc: npcName,
    })
      .then((res) => {
        const r = res.data && res.data.room;
        if (r) pushLocalSystem(`${npcName.replace('_',' ')} has prepared a private room. Type /accept to follow.`);
      })
      .catch((err) => {
        const d = err && err.response && err.response.data && err.response.data.detail;
        pushLocalSystem(d || `${npcName.replace('_',' ')} declined.`);
      });
    return true;
  }

  // Msg #796 → Msg #975 — /recall {NPC} [room] — ALL users can now recall an NPC.
  m = text.match(/^\/recall\s+(\S+)(?:\s+(\S+))?/i);
  if (m) {
    axios.post(`${API}/admin/recall_npc`, {
      requester: username, npc: m[1], room: m[2] || room,
    })
      .then((r) => pushLocalSystem(`Recalled ${m[1].replace('_',' ')} to ${r.data.room}.`))
      .catch((err) => {
        const d = err && err.response && err.response.data && err.response.data.detail;
        pushLocalSystem(d || 'Recall failed.');
      });
    return true;
  }

  // Msg #975 — /lockin {NPC}  — that NPC will respond only to you in this room.
  //            /lockin off    — release any lockin bound to you in this room.
  m = text.match(/^\/lockin\s+(\S+)(?:\s+(off))?$/i);
  if (m) {
    const arg = m[1];
    const off = (arg.toLowerCase() === 'off') || (m[2] && m[2].toLowerCase() === 'off');
    if (off) {
      // Release ALL lockins in this room caller owns.
      axios.post(`${API}/npc/lockin`, {
        requester: username, npc: arg.toLowerCase() === 'off' ? '' : arg, room, off: true,
      }).catch(() => {});
      pushLocalSystem('Lockin released.');
      return true;
    }
    axios.post(`${API}/npc/lockin`, {
      requester: username, npc: arg, room, off: false,
    })
      .then(() => pushLocalSystem(`Locked ${arg.replace('_', ' ')} to you in ${room}. /lockin off to release.`))
      .catch((err) => {
        const d = err && err.response && err.response.data && err.response.data.detail;
        pushLocalSystem(d || 'Lockin failed.');
      });
    return true;
  }

  // Msg #975 — /terminate {NPC} — sends the NPC to a random OTHER room.
  m = text.match(/^\/terminate\s+(\S+)/i);
  if (m) {
    axios.post(`${API}/npc/terminate`, {
      requester: username, npc: m[1], room,
    })
      .then((r) => pushLocalSystem(`${m[1].replace('_', ' ')} was sent to ${r.data.to}.`))
      .catch((err) => {
        const d = err && err.response && err.response.data && err.response.data.detail;
        pushLocalSystem(d || 'Terminate failed.');
      });
    return true;
  }

  // Msg #976 — /orgy [prompt]  — chained NPC scene in a private room
  // Msg #978 — Drop the "must be in a private room" gate. Backend now
  // auto-locates any private_* room already hosting 2+ NPCs; if it finds
  // one we switch the caller into that room and initiate.
  m = text.match(/^\/orgy(?:\s+(.+))?$/i);
  if (m) {
    axios.post(`${API}/npc/orgy`, {
      requester: username, room, prompt: m[1] || '', rounds: 3,
    })
      .then((r) => {
        const tgt = r.data && r.data.room;
        const list = (r.data && r.data.npcs) || [];
        if (tgt && tgt !== room) {
          setRoom(tgt);
          sessionStorage.setItem('keep_room', tgt);
          pushLocalSystem(`Slipping into ${tgt} — orgy scene begins with ${list.map(n => n.replace('_', ' ')).join(', ')}.`);
        } else {
          pushLocalSystem(`Orgy scene begins with ${list.map(n => n.replace('_', ' ')).join(', ')}.`);
        }
      })
      .catch((err) => {
        const d = err && err.response && err.response.data && err.response.data.detail;
        pushLocalSystem(d || '/orgy failed.');
      });
    return true;
  }

  // Msg #978 — /arouse {NPC}  — bias an NPC toward initiating Erotic RP with you
  m = text.match(/^\/arouse\s+(\S+)(?:\s+(\d+))?$/i);
  if (m) {
    const target = m[1];
    const minutes = m[2] ? parseInt(m[2], 10) : 30;
    axios.post(`${API}/npc/arouse`, {
      requester: username, npc: target, room, minutes,
    })
      .then((r) => {
        const pretty = String((r.data && r.data.npc) || target).replace('_', ' ');
        pushLocalSystem(`${pretty} eyes you with a slow, warm smile — for the next ${(r.data && r.data.minutes) || minutes} minutes their attention is yours to press.`);
      })
      .catch((err) => {
        const d = err && err.response && err.response.data && err.response.data.detail;
        pushLocalSystem(d || '/arouse failed.');
      });
    return true;
  }

  // Msg #983 — /nowplaying {title}   set the shared "Now Watching" bar for this room.
  // Empty title clears the bar. Primarily used in Movie_Night.
  m = text.match(/^\/nowplaying(?:\s+(.+))?$/i);
  if (m) {
    const title = (m[1] || '').trim();
    axios.post(`${API}/room/nowplaying`, {
      room, user: username, title,
    })
      .then(() => {
        if (title) pushLocalSystem(`🎬 Marquee set for ${room}: “${title}”.`);
        else pushLocalSystem(`🎬 Marquee cleared for ${room}.`);
      })
      .catch((err) => {
        const d = err && err.response && err.response.data && err.response.data.detail;
        pushLocalSystem(d || '/nowplaying failed.');
      });
    return true;
  }

  // Msg #979 — /pray {Anu|Danu}   priestess prayer: 3 HP over 90s
  m = text.match(/^\/pray\s+(\S+)$/i);
  if (m) {
    const target = m[1];
    axios.post(`${API}/npc/pray`, {
      user_id: userId, requester: username, npc: target, room,
    })
      .then((r) => {
        const pretty = String((r.data && r.data.npc) || target).replace('_', ' ');
        pushLocalSystem(`${pretty} lays her hands on you — prayer begins (+1 HP every 30s, 3 ticks).`);
      })
      .catch((err) => {
        const d = err && err.response && err.response.data && err.response.data.detail;
        pushLocalSystem(d || '/pray failed.');
      });
    return true;
  }

  // Msg #999 — /magic {NPC?}   attack via spell — 1d20 hit ≥12 for 2 HP, 1 MP + 1 HP recoil (waived at Veteran+)
  m = text.match(/^\/magic(?:\s+(\S+))?$/i);
  if (m) {
    const target = m[1] || (pickResponder(text) || '');
    if (!target) {
      pushLocalSystem('/magic needs a target NPC in the room. Usage: /magic {NPC}.');
      return true;
    }
    axios.post(`${API}/npc/combat/attack`, {
      user_id: userId, npc: target, action: 'magic',
    })
      .then((r) => {
        const d = r.data || {};
        const pretty = String(target).replace('_', ' ');
        const recoilNote = d.self_recoil ? ` (-${d.self_recoil} HP recoil)` : ' (no recoil — Veteran+)';
        if (d.hit) {
          pushLocalSystem(`🔮 Magic strike (d20=${d.roll}, need 12+). HIT ${pretty} for ${d.damage} HP${recoilNote}. Your HP: ${d.user_hp_after}/${d.user_hp_max}. MP: ${d.mp_after}/${d.mp_max}.${d.killed ? ` ${pretty} falls!` : ''}`);
        } else {
          pushLocalSystem(`🔮 Magic strike (d20=${d.roll}, need 12+). MISS${recoilNote}. Your HP: ${d.user_hp_after}/${d.user_hp_max}. MP: ${d.mp_after}/${d.mp_max}.`);
        }
      })
      .catch((err) => {
        const dd = err && err.response && err.response.data && err.response.data.detail;
        pushLocalSystem(dd || '/magic failed.');
      });
    return true;
  }

  // Msg #979 — /heal   priest/healer self-heal spell — costs 1 MP, +2 HP
  m = text.match(/^\/heal$/i);
  if (m) {
    const anyNpc = (liveNpcs[room] || [])[0]?.name || 'Anu';
    axios.post(`${API}/npc/combat/attack`, {
      user_id: userId, npc: anyNpc, action: 'heal',
    })
      .then((r) => {
        const d = r.data || {};
        pushLocalSystem(`✨ Heal cast. +${d.heal} HP → ${d.user_hp_after}/${d.user_hp_max}. MP: ${d.mp_after}/${d.mp_max}.`);
      })
      .catch((err) => {
        const dd = err && err.response && err.response.data && err.response.data.detail;
        pushLocalSystem(dd || '/heal failed.');
      });
    return true;
  }

  // Msg #802 — /dismiss {NPC} — admin kicks an NPC/monster out of the room.
  m = text.match(/^\/dismiss\s+(\S+)/i);
  if (m) {
    if (!isAdmin) { pushLocalSystem('Admin only.'); return true; }
    axios.post(`${API}/npc/dismiss`, {
      requester: username, npc: m[1], room,
    })
      .then(() => pushLocalSystem(`Dismissed ${m[1].replace('_', ' ')} from ${room}.`))
      .catch((err) => {
        const d = err && err.response && err.response.data && err.response.data.detail;
        pushLocalSystem(d || 'Dismiss failed.');
      });
    return true;
  }

  // Msg #796 — /like {NPC}  or /dislike {NPC}  — manual affection adjust
  m = text.match(/^\/(like|dislike)\s+(\S+)/i);
  if (m) {
    const delta = m[1].toLowerCase() === 'like' ? 1 : -1;
    axios.post(`${API}/npc/affection/adjust`, {
      user_id: userId, user_name: displayName(),
      npc: m[2], delta,
    })
      .then((r) => pushLocalSystem(
        `${m[2].replace('_',' ')}: 💚 ${r.data.affection}  💢 ${r.data.dislike}`,
      ))
      .catch((err) => {
        const d = err && err.response && err.response.data && err.response.data.detail;
        pushLocalSystem(d || 'Adjust failed.');
      });
    return true;
  }

  // ── /private {room} {password} — create a private password-locked room ──
  m = text.match(/^\/private\s+(\S+)\s+(\S+)$/i);
  if (m) {
    const newRoom = m[1].replace(/\s+/g, '_');
    const pwd = m[2];
    axios.post(`${API}/rooms/private/create`, {
      owner: username, owner_user_id: userId, room: newRoom, password: pwd,
    })
      .then(() => {
        setKnownPrivateRooms((prev) => {
          const next = [...prev.filter((r) => r.name !== newRoom), { name: newRoom, password: pwd }];
          localStorage.setItem('mandala_known_private_rooms', JSON.stringify(next));
          return next;
        });
        setRoom(newRoom);
sessionStorage.setItem('keep_room', newRoom);
        pushLocalSystem(`Private room "${newRoom}" created with password.`);
      })
      .catch((err) => {
        const d = err && err.response && err.response.data && err.response.data.detail;
        pushLocalSystem(d || 'Could not create private room.');
      });
    return true;
  }

  // ── /describe {room description} — owner/admin sets room description ──
  m = text.match(/^\/describe\s+(.+)$/i);
  if (m) {
    const desc = m[1];
    axios.post(`${API}/rooms/describe`, {
      requester: username, requester_user_id: userId, room, description: desc,
    })
      .then(() => {
        setRoomDescription(desc);
        pushLocalSystem(`Room description updated for ${room}.`);
        // Backend also posts a Narrator chat line so other users in the room see the new ambiance.
      })
      .catch((err) => {
        const d = err && err.response && err.response.data && err.response.data.detail;
        pushLocalSystem(d || 'Could not update room description.');
      });
    return true;
  }

  // ── /promote {user} — admin: make a user a mod of current room ──
  m = text.match(/^\/promote\s+(\S+)$/i);
  if (m) {
    if (!isAdmin) { pushLocalSystem('Admin only.'); return true; }
    axios.post(`${API}/admin/promote`, { requester: username, target: m[1], room })
      .then(() => pushLocalSystem(`Promoted ${m[1]} to moderator of ${room}.`))
      .catch(() => pushLocalSystem('Promote failed.'));
    return true;
  }

  // ── /demote [*] {user} — admin: remove mod for current room or globally with * ──
  m = text.match(/^\/demote\s+(\*\s+)?(\S+)$/i);
  if (m) {
    if (!isAdmin) { pushLocalSystem('Admin only.'); return true; }
    const global = !!m[1];
    axios.post(`${API}/admin/demote`, {
      requester: username, target: m[2], room: global ? '*' : room,
    })
      .then(() => pushLocalSystem(`Demoted ${m[2]} ${global ? 'globally' : `in ${room}`}.`))
      .catch(() => pushLocalSystem('Demote failed.'));
    return true;
  }

  // /accept — accept any pending NPC private-invite
  if (/^\/accept(\s|$)/i.test(text)) {
    axios.post(`${API}/npc/accept_invite`, {
      user_id: userId, user_name: displayName(),
    })
      .then((res) => {
        const r = res.data && res.data.room;
        if (r) {
          setRoom(r);
          sessionStorage.setItem('keep_room', r);
          pushLocalSystem(`You follow ${res.data.npc.replace('_', ' ')} into ${r}.`);
        }
      })
      .catch((err) => {
        const d = err && err.response && err.response.data && err.response.data.detail;
        pushLocalSystem(d || 'No pending invitation.');
      });
    return true;
  }
  // /hire NPC_Name offer text — invite an NPC to a private room w/ gold/coin offer
  m = text.match(/^\/hire\s+(\S+)\s+([\s\S]+)$/i);
  if (m) {
    const npc = m[1];
    const offer = m[2];
    // Random hex room ID — no NPC/user info leaked in the room name.
    const hexBytes = window.crypto && window.crypto.getRandomValues
      ? Array.from(window.crypto.getRandomValues(new Uint8Array(5)))
      : Array.from({ length: 5 }, () => Math.floor(Math.random() * 256));
    const privateRoom = 'private_' + hexBytes.map((b) => b.toString(16).padStart(2, '0')).join('');
    axios.post(`${API}/npc/invite_private`, {
      requester_user_id: userId,
      requester_name: displayName(),
      npc,
      room: privateRoom,
      offer,
    })
      .then((res) => {
        const finalRoom = (res.data && res.data.room) || privateRoom;
        setRoom(finalRoom);
        sessionStorage.setItem('keep_room', finalRoom);
        pushLocalSystem(`${npc.replace('_', ' ')} agreed to join you in a private room.`);
      })
      .catch((err) => {
        const detail = err && err.response && err.response.data && err.response.data.detail;
        pushLocalSystem(detail || `${npc} declined the offer.`);
      });
    return true;
  }
  // Admin: speak AS an NPC.  /as NPC_Name [/me ]message
  m = text.match(/^\/as\s+(\S+)\s+([\s\S]+)$/i);
  if (m) {
    if (!isAdmin) { pushLocalSystem('Admin only.'); return true; }
    let body = m[2], action = false;
    const meMatch = body.match(/^\/me\s+([\s\S]+)$/i);
    if (meMatch) { body = meMatch[1]; action = true; }
    axios.post(`${API}/admin/say_as`, {
      requester: username, character: m[1], room, text: body, is_action: action,
    }).catch(() => pushLocalSystem(`Could not post as ${m[1]}.`));
    return true;
  }

  // ── /join {room} [password] — public room OR known private room ──
  m = text.match(/^\/(join|go)\s+(\S+)(?:\s+(\S+))?$/i);
  if (m) {
    const target = m[2].replace(/\s+/g, '_');
    const pwd = m[3];
    if (DEFAULT_ROOMS.includes(target) || PUBLIC_ROOMS.includes(target)) {
      setRoom(target); sessionStorage.setItem('keep_room', target);
      pushLocalSystem(`Joined ${target}.`);
      return true;
    }
    // Try private room (or use last-known password if not supplied)
    const known = knownPrivateRooms.find((x) => x.name.toLowerCase() === target.toLowerCase());
    axios.post(`${API}/rooms/private/join`, {
      room: target,
      password: pwd || (known && known.password) || '',
      // Msg #774 — cloaked admins bypass the password check.
      requester: (isAdmin && cloaked) ? username : undefined,
    })
      .then((res) => {
        const r = res.data && res.data.room;
        if (r) {
          setRoom(r); sessionStorage.setItem('keep_room', r);
          setKnownPrivateRooms((prev) => {
            const next = [...prev.filter((x) => x.name !== r), { name: r, password: pwd || '' }];
            localStorage.setItem('mandala_known_private_rooms', JSON.stringify(next));
            return next;
          });
          const shadow = res.data && res.data.admin_shadow;
          pushLocalSystem(shadow
            ? `Shadow-joined ${r} (invisible admin observer).`
            : `Joined private room ${r}.`);
        }
      })
      .catch((err) => {
        const d = err && err.response && err.response.data && err.response.data.detail;
        pushLocalSystem(d || `Cannot join "${target}".`);
      });
    return true;
  }
  m = text.match(/^\/roll\s+(.+)$/i);
  if (m) {
    const arg = m[1].trim().toLowerCase();
    if (arg === 'passfail') { const r = Math.random() < 0.5 ? 'PASS' : 'FAIL'; sendToServer(`flips a coin... ${r}.`, true); }
    else {
      const dm = arg.match(/^(\d+)d(\d+)([+-]\d+)?$/);
      if (!dm) { pushLocalSystem('Usage: /roll NdD+X or /roll passfail.'); return true; }
      const n = parseInt(dm[1], 10), d = parseInt(dm[2], 10), mod = parseInt(dm[3] || '0', 10);
      if (n > 50 || d > 1000) { pushLocalSystem('Roll too large.'); return true; }
      const rolls = Array.from({ length: n }, () => 1 + Math.floor(Math.random() * d));
      const total = rolls.reduce((a, b) => a + b, 0) + mod;
      sendToServer(`rolls ${n}d${d}${mod ? (mod > 0 ? '+' + mod : mod) : ''}: [${rolls.join(', ')}] = ${total}.`, true);
    }
    return true;
  }
  // /leaderboard  /top  — show top combatants by damage dealt
  m = text.match(/^\/(leaderboard|top)(?:\s+(\d+))?/i);
  if (m) {
    const lim = Math.max(1, Math.min(25, parseInt(m[2] || '10', 10)));
    axios.get(`${API}/combat/top?limit=${lim}`)
      .then((res) => {
        const list = (res.data && res.data.top) || [];
        if (list.length === 0) { pushLocalSystem('No combat records yet — go /attack Golem!'); return; }
        pushLocalSystem('⚔ TOP COMBATANTS OF MANDALA ⚔');
        list.forEach((row, i) => {
          pushLocalSystem(`  ${i + 1}. ${row.username}  —  ${row.damage_dealt} dmg, ${row.hits} hits, ${row.kills} kills`);
        });
      })
      .catch(() => pushLocalSystem('Could not fetch leaderboard.'));
    return true;
  }
  // /attack {npc}  — RPG combat: 1d12 - defense. Roll 5 or lower auto-miss; otherwise (roll-def)>=6 => 1 damage.
  m = text.match(/^\/attack\s+(\S+)(?:\s+(\d+))?/i);
  if (m) {
    const target = m[1].trim();
    const npcMatch = ALL_NPC_DIRECTORY.find((n) => String(n.name).toLowerCase() === target.toLowerCase());
    if (!npcMatch) { pushLocalSystem(`Unknown target "${target}". You can only /attack an NPC or monster.`); return true; }
    // Attach username to leaderboard row (idempotent)
    axios.post(`${API}/combat/register_name`, { user_id: userId, username }).catch(() => {});
    axios.post(`${API}/npc/combat/attack`, { user_id: userId, npc: npcMatch.name, action: 'attack' })
      .then((res) => {
        const r = res.data || {};
        const name = npcMatch.name.replaceAll('_', ' ');
        if (r.hit) {
          const killed = r.hp_after === 0;
          sendToServer(`attacks ${name} — 1d12=${r.roll}, defense −${r.defense}, net ${r.net}. STRIKE lands for 1 damage.${killed ? ` ${name} falls!` : ''} (HP ${r.hp_after}/${r.hp_max})`, true);
        } else if (r.roll <= 5) {
          sendToServer(`attacks ${name} — 1d12=${r.roll}. MISS (roll ≤ 5). (HP ${r.hp_after}/${r.hp_max})`, true);
        } else {
          sendToServer(`attacks ${name} — 1d12=${r.roll}, defense −${r.defense}, net ${r.net}. BLOCKED (need net ≥ 6). (HP ${r.hp_after}/${r.hp_max})`, true);
        }
        // Msg #904 — dice-gated monster reaction: after our roll settles, the monster reacts IC.
        // Only fire on successful strikes (hit) OR true misses (net<6 OR roll<=5), skipping edge no-ops.
        const reactHit = !!r.hit;
        axios.post(`${API}/combat/react`, {
          user_id: userId, username, room: npcMatch.room || 'MainHall',
          npc: npcMatch.name, hit: reactHit, roll: r.roll, net: r.net,
          hp_after: r.hp_after, hp_max: r.hp_max,
        }).catch(() => {});
      })
      .catch(() => pushLocalSystem('Combat failed. Try again.'));
    return true;
  }
  // /combat {npc}  — Msg #904: summon Referee_R to oversee a formal dice-duel with a monster/NPC.
  m = text.match(/^\/combat\s+(\S+)/i);
  if (m) {
    const target = m[1].trim();
    const npcMatch = ALL_NPC_DIRECTORY.find((n) => String(n.name).toLowerCase() === target.toLowerCase());
    if (!npcMatch) { pushLocalSystem(`Unknown target "${target}". /combat only works on NPCs or monsters.`); return true; }
    axios.post(`${API}/combat/summon_referee`, {
      user_id: userId, username, room, npc: npcMatch.name,
    })
      .then(() => {
        pushLocalSystem(`Referee_R has entered ${room}. Type /attack ${npcMatch.name} to strike — the fight is on.`);
      })
      .catch(() => pushLocalSystem('Referee_R could not be summoned. Try again.'));
    return true;
  }
  // /badending or /bad-ending {npc}  — Msg #904 + #802: monster restrains + fondles + invites to a private room.
  m = text.match(/^\/bad-?ending\s+(\S+)/i);
  if (m) {
    const target = m[1].trim();
    const npcMatch = ALL_NPC_DIRECTORY.find((n) => String(n.name).toLowerCase() === target.toLowerCase());
    if (!npcMatch) { pushLocalSystem(`Unknown target "${target}".`); return true; }
    axios.post(`${API}/combat/badending`, {
      user_id: userId, username, room, npc: npcMatch.name,
    })
      .then((res) => {
        const priv = res.data && res.data.private_room;
        if (priv) {
          pushLocalSystem(`${npcMatch.name.replaceAll('_', ' ')} beckons you to ${priv}. Type /accept ${npcMatch.name} to join them.`);
        }
      })
      .catch(() => pushLocalSystem('Nothing happens...'));
    return true;
  }
  // /heal {npc}  — restore HP to an NPC/monster
  m = text.match(/^\/heal\s+(\S+)(?:\s+(\d+))?/i);
  if (m) {
    const target = m[1].trim();
    const amount = Math.max(1, Math.min(15, parseInt(m[2] || '4', 10)));
    const npcMatch = ALL_NPC_DIRECTORY.find((n) => String(n.name).toLowerCase() === target.toLowerCase());
    if (!npcMatch) { pushLocalSystem(`Unknown target "${target}". /heal only works on NPCs.`); return true; }
    axios.post(`${API}/npc/combat/attack`, { user_id: userId, npc: npcMatch.name, action: 'heal', amount })
      .then((res) => {
        const r = res.data || {};
        sendToServer(`heals ${npcMatch.name.replaceAll('_', ' ')} for ${-r.damage} HP. (HP ${r.hp_after}/${r.hp_max})`, true);
      })
      .catch(() => pushLocalSystem('Heal failed. Try again.'));
    return true;
  }
  // Admin commands (backend will enforce too)
  m = text.match(/^\/kick\s+(\S+)/i);
  if (m) {
    // Msg #774 — Moderators can also kick, but choose a duration first.
    if (!isAdmin && !isMod) { pushLocalSystem('Moderator or Admin only.'); return true; }
    setKickModal({ target: m[1], action: 'kick' });
    return true;
  }
  // /ban [*] {user} — admin/mod global banish; pops duration modal
  m = text.match(/^\/ban(?:\s+\*)?\s+(\S+)/i);
  if (m) {
    if (!isAdmin && !isMod) { pushLocalSystem('Moderator or Admin only.'); return true; }
    setKickModal({ target: m[1], action: 'ban' });
    return true;
  }
  m = text.match(/^\/unban\s+(\S+)/i);
  if (m) {
    if (!isAdmin) { pushLocalSystem('Admin only.'); return true; }
    axios.post(`${API}/admin/unban`, { requester: username, target: m[1] })
      .then(() => pushLocalSystem(`Unbanned ${m[1]}.`))
      .catch(() => pushLocalSystem('Unban failed.'));
    return true;
  }
  if (/^\/clearroom(\s|$)/i.test(text)) {
    if (!isAdmin) { pushLocalSystem('Admin only.'); return true; }
    axios.post(`${API}/admin/clearroom`, { requester: username, room })
      .then(() => pushLocalSystem(`Cleared messages in ${room}.`))
      .catch(() => pushLocalSystem('Clear failed.'));
    return true;
  }
  // Msg #996 — /throwball <pokemon>  — Attempt a Poké Ball catch.
  //  Requires the target's HP < 5 in your fight. 50/50 coin flip.
  m = text.match(/^\/throwball\s+(\S+)/i);
  if (m) {
    const target = m[1].trim();
    axios.post(`${API}/pokemon/throwball`, { username, npc: target, room })
      .then((res) => {
        const r = res.data || {};
        if (r.ok === false && r.reason === 'hp_too_high') {
          pushLocalSystem(`⚪ ${r.message}`);
        } else if (r.caught) {
          sendToServer(`throws a Poké Ball at ${target}. It shakes… one… two… three… CLICK! Gotcha!`, true);
          pushLocalSystem(r.message || `🎉 You caught ${target}!`);
        } else {
          sendToServer(`throws a Poké Ball at ${target}. It shakes once, twice… and pops open! ${target} broke free!`, true);
          pushLocalSystem(r.message || `⚪ Missed the catch.`);
        }
      })
      .catch((e) => {
        const d = e?.response?.data?.detail;
        pushLocalSystem(d || 'Throw failed.');
      });
    return true;
  }
  // Msg #996 — /stadium challenge <opponent> <pokemon>
  //            /stadium accept <battle_id> <pokemon>
  //            /stadium status
  m = text.match(/^\/stadium\s+(\w+)(?:\s+(\S+))?(?:\s+(\S+))?/i);
  if (m) {
    if (room !== 'Pokemon_Stadium') {
      pushLocalSystem('You must be in Pokemon_Stadium to use /stadium.');
      return true;
    }
    const sub = m[1].toLowerCase();
    if (sub === 'challenge') {
      const opp = m[2], pk = m[3];
      if (!opp || !pk) { pushLocalSystem('Usage: /stadium challenge <opponent> <pokemon>'); return true; }
      axios.post(`${API}/pokemon/stadium/challenge`, { requester: username, opponent: opp, pokemon: pk })
        .then((res) => pushLocalSystem(`⚔ Challenge sent! Battle ID: ${res.data?.battle_id}`))
        .catch((e) => pushLocalSystem(e?.response?.data?.detail || 'Challenge failed.'));
      return true;
    }
    if (sub === 'accept') {
      const bid = m[2], pk = m[3];
      if (!bid || !pk) { pushLocalSystem('Usage: /stadium accept <battle_id> <pokemon>'); return true; }
      axios.post(`${API}/pokemon/stadium/accept`, { battle_id: bid, user: username, pokemon: pk })
        .then(() => pushLocalSystem(`Accepted battle ${bid}!`))
        .catch((e) => pushLocalSystem(e?.response?.data?.detail || 'Accept failed.'));
      return true;
    }
    if (sub === 'status') {
      axios.get(`${API}/pokemon/stadium/active`, { params: { username } })
        .then((res) => {
          const b = res.data?.battle;
          if (!b) pushLocalSystem('No active battle.');
          else pushLocalSystem(`Battle ${b.battle_id} — ${b.p1.user} (${b.p1.pokemon} ${b.p1.hp}/${b.p1.hp_max}) vs ${b.p2.user} (${b.p2.pokemon || '—'} ${b.p2.hp}/${b.p2.hp_max}). Turn: ${b.turn}`);
        })
        .catch(() => pushLocalSystem('Status check failed.'));
      return true;
    }
    pushLocalSystem('Usage: /stadium challenge|accept|status …');
    return true;
  }
  return false;
};
